/**
 * Abstract class providing different Helper functions.
 * @abstract
 */
class Helper {
	/**
	 * Checks if given parameter is an object.
	 * @param {*} checkMe - Parameter to be checked.
	 * @returns {boolean} If parameter is an object.
	 * @static
	 */
	static isObject (checkMe) {
		return checkMe && checkMe.constructor.name === 'Object';
	}

	/**
	 * Clones an object if given param is object. Else the value itself will be returned.
	 * NO DEEP CLONING PROVIDED.
	 * @param {Object} cloneMe - Object to be cloned.
	 * @returns {Object} The Cloned object.
	 */
	static shallowClone (cloneMe) {
		if (Helper.isObject(cloneMe)) {
			return JSON.parse(JSON.stringify(cloneMe));
		}

		return cloneMe;
	}

	/**
	 * Merges given objects.
	 * @param {Object} mergeUs - Array of objects to be merged.
	 * @returns {Object} The merged Object.
	 */
	static mergeObjects (...mergeUs) {
		let merged = {};
		for (let mergeMe of mergeUs) {
			for (let key in mergeMe) {
				if (mergeMe.hasOwnProperty(key)) {
					merged[key] = mergeMe[key];
				}
			}
		}

		return merged;
	}

	/**
	 * Gets parameter from URL as a value.
	 * @param {string} param - Parameter to be read.
	 * @returns {string|number|null} Value of Parameter if parameter available. Null if parameter is not present. 0 if parameter has no value.
	 */
	static getUrlParam (param) {
		const results = new RegExp('[?&]' + param + '=([^]*)').exec(window.location.href);
		if (results === null) {
			return null;
		} else {
			return results[1] || 0;
		}
	}

	/**
	 * Returns parameter Object as an URL String.
	 * @param {Object} params - The Object containing the parameter keys and values.
	 * @returns {string} The URL string.
	 */
	static getParamsAsString (params) {
		const paramsArray = [];

		for (let key in params) {
			if (params.hasOwnProperty(key)) {
				if (typeof params[key] === 'function') {
					paramsArray.push(key + '=' + params[key]());
				} else {
					paramsArray.push(key + '=' + params[key]);
				}
			}
		}

		return paramsArray.join('&');
	}

	/**
	 * Returns element with given attribute.
	 * Calls itself recursively with parent element until element with attribute was found.
	 * @param element {Element} - Element to look for attribute.
	 * @param attributeName {string} - Attribute to return element for.
	 * @returns {undefined|Element} Returns undefined if given element and parents don't have given attribute name. Returns element if attribute found.
	 */
	static getElementByAttribute (element, attributeName) {
		if (!element) return;

		if (element === document.body) {
			return;
		}
		if (element.matches(attributeName)) {
			return element;
		} else {
			return Helper.getElementByAttribute(element.parentElement, attributeName);
		}
	}

	/**
	 * Returns given attribute value.
	 * Calls itself recursively with parent element until element with attribute was found.
	 * @param element {Element} - Element to look for attribute.
	 * @param attributeName {string} - Attribute to return value for.
	 * @returns {undefined|string} Returns undefined if given element and parents don't have given attribute name. Returns value if found.
	 */
	static getAttributeValue (element, attributeName) {
		if (!element) return;

		if (element === document.body) {
			return;
		}

		if (element.hasAttribute(attributeName)) {
			return element.getAttribute(attributeName);
		} else {
			return Helper.getAttributeValue(element.parentNode, attributeName);
		}
	}

	/**
	 * Sets all checked inputs to unchecked state.
	 * @param {Event} event - The click event fired by caller
	 * @param {Element} preventUncheckElement - The Element which should not be closed on click
	 */
	static uncheckOnOutsideClick (event, preventUncheckElement) {
		let checkbox = preventUncheckElement.querySelector('input:checked');
		if (checkbox && !preventUncheckElement.contains(event.target)) {
			checkbox.checked = false;
		}
	}

	/**
	 * Creates a promise and starts XMLHttpRequest inside.
	 * @param {string} url The URL to call
	 * @param {'POST' | 'GET'} method The method to use for request
	 * @param {boolean} withCredentials Should credentials, e.g. cookies, be send with the request
	 * @returns {Promise<XMLHttpRequest>}
	 */
	static xhr (url, method = 'GET', withCredentials = true) {
		const request = new XMLHttpRequest();
		request.withCredentials = withCredentials;
		return new Promise((resolve, reject) => {
			request.onreadystatechange = function () {
				if (request.readyState !== 4) {
					return;
				}
				if (request.status >= 200 && request.status < 300) {
					resolve(request);
				} else {
					reject(request);
				}
			};
			request.open(method, url, true);
			request.send();
		});
	}

	/**
	 * Checks if stage is live stage.
	 * @returns {boolean} If stage is 'live'.
	 */
	static isLiveStage () {
		return window.PageConfig.stage === 'live' || window.PageConfig.stage === 'prelive';
	}

	/**
	 * Checks if element is visible. implicit also checks its parents elements for visibility.
	 * @param {Element} element - The element to check.
	 * @returns {boolean} If the object is visible.
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
	 */
	static isElementVisible (element) {
		return element.offsetParent !== null;
	}

	/**
	 * Reads first party cookie with given name.
	 * @param {string} cookieName - The Cookie name.
	 * @returns {string} The cookie Value. Empty if cookie not set.
	 */
	static getCookieValue (cookieName) {
		const findMe = '(?:^|; )' + cookieName + '=([^;]*)';
		return (document.cookie.match(findMe) || [0, ''])[1];
	}

	/**
	 * Builds a script tag and adds it to the head tag
	 * @param {string} scriptSrc - The script URL.
	 * @param {string} [callback] - A optional callback which runs after the script was loaded.
	 * @returns {string} The cookie Value. Empty if cookie not set.
	 */
	static addScript (scriptSrc, callback) {
		if (!scriptSrc) return;

		let head = document.getElementsByTagName('head')[0];
		let script = document.createElement('script');
		script.type = 'text/javascript';

		script.onload = (event) => {
			callback && callback();
		};

		script.src = scriptSrc;
		head.appendChild(script);
	}
}

export { Helper };
